import type {
  GroupWithRegimes,
  RegimeGroup,
  RegimeItem,
} from 'containers/admin/clients/client/tabs-components/aid-studio-tab/aid-studio-details/steps/matrix-step/hooks/regime-sidebar-types';

export type SidebarDragType = 'group' | 'regime';

export interface SidebarDragData {
  type: SidebarDragType;
  entityId: string | number;
  groupId?: string | number;
}

interface SidebarDragNode {
  id: string | number;
  data: { current: unknown };
}

export interface SidebarDragEvent {
  active: SidebarDragNode;
  over?: SidebarDragNode | null;
}

const PREVIEW_SORT_ORDER_STEP = 100;

export const getGroupSortableId = (groupId: string | number): string =>
  `group:${groupId}`;

export const getRegimeSortableId = (regimeId: string | number): string =>
  `regime:${regimeId}`;

export const isSidebarDragData = (value: unknown): value is SidebarDragData => {
  if (!value || typeof value !== 'object') return false;

  const data = value as Partial<SidebarDragData>;

  return (
    (data.type === 'group' || data.type === 'regime') &&
    (typeof data.entityId === 'string' || typeof data.entityId === 'number')
  );
};

export const buildGroupsWithRegimes = (
  groups: RegimeGroup[],
  regimes: RegimeItem[],
  keepEmptyGroups = false
): GroupWithRegimes[] => {
  const sorted = [...groups].sort((a, b) => a.sortOrder - b.sortOrder);
  const mapped = sorted.map((group) => ({
    ...group,
    regimes: regimes
      .filter((r) => r.groupId === group.id)
      .sort((a, b) => a.sortOrder - b.sortOrder),
  }));

  return keepEmptyGroups
    ? mapped
    : mapped.filter((group) => !!group.regimes.length);
};

export const applyRegimePlacement = (
  currentRegimes: RegimeItem[],
  currentGroups: RegimeGroup[],
  activeRegimeId: string | number,
  overData: SidebarDragData
): RegimeItem[] | null => {
  const activeRegime = currentRegimes.find((r) => r.id === activeRegimeId);
  const targetGroupId =
    overData.type === 'group' ? overData.entityId : overData.groupId;

  if (!activeRegime || targetGroupId === undefined) return null;

  const groupsWithRegimes = buildGroupsWithRegimes(
    currentGroups,
    currentRegimes,
    true
  );
  const sourceGroup = groupsWithRegimes.find(
    (g) => g.id === activeRegime.groupId
  );
  const targetGroup = groupsWithRegimes.find((g) => g.id === targetGroupId);

  if (!sourceGroup || !targetGroup) return null;

  const sourceRegimes = sourceGroup.regimes.filter(
    (r) => r.id !== activeRegimeId
  );
  const targetRegimes = targetGroup.regimes.filter(
    (r) => r.id !== activeRegimeId
  );
  let insertionIndex = targetRegimes.length;

  if (overData.type === 'regime') {
    insertionIndex = targetRegimes.findIndex((r) => r.id === overData.entityId);

    if (sourceGroup.id === targetGroup.id) {
      const activeIndex = sourceGroup.regimes.findIndex(
        (r) => r.id === activeRegimeId
      );
      const overIndex = sourceGroup.regimes.findIndex(
        (r) => r.id === overData.entityId
      );

      if (activeIndex >= 0 && overIndex >= 0 && activeIndex < overIndex) {
        insertionIndex += 1;
      }
    }
  }

  if (insertionIndex < 0) return null;

  const nextTargetRegimes = [
    ...targetRegimes.slice(0, insertionIndex),
    { ...activeRegime, groupId: targetGroupId },
    ...targetRegimes.slice(insertionIndex),
  ];
  const affectedRegimesById = new Map<string | number, RegimeItem>();

  sourceRegimes.forEach((regime, index) => {
    affectedRegimesById.set(regime.id, {
      ...regime,
      sortOrder: (index + 1) * PREVIEW_SORT_ORDER_STEP,
    });
  });
  nextTargetRegimes.forEach((regime, index) => {
    affectedRegimesById.set(regime.id, {
      ...regime,
      groupId: targetGroupId,
      sortOrder: (index + 1) * PREVIEW_SORT_ORDER_STEP,
    });
  });

  return currentRegimes.map(
    (regime) => affectedRegimesById.get(regime.id) ?? regime
  );
};
